import * as React from 'react';
import type {
  WebsocketMessage,
  NewMessagesMsg,
  ThreadUpdateMsg,
} from 'src/types/websocket';
import type {Router} from 'src/types/router';
import type {MessagePending} from 'src/types/messages';
import {INBOX_TYPE} from 'src/types/messages';
import type {State, Dispatch, GetState} from 'src/reducers';

import values from 'lodash/values';
import filter from 'lodash/filter';
import omit from 'lodash/omit';
import {UPDATE_AGENT_JOINED_QUEUE} from 'src/chat-extension/messages';

import {batch, type BatchAction} from 'src/action-creators/batch';
import {
  changeReadOnlyMode,
  getInbox,
  receiveMessages,
  updateUnreadCounts,
  fetchMessagesForPhone,
  receiveSchedulerEvent,
  receiveConversationContextEvents,
  receiveTypingEvent,
  receiveReadStatusEvent,
  setReceivedPhoneNumberEvent,
  setInboxFilter,
} from 'src/action-creators/messages';
import {
  receivePerformanceDeliveryAnalytics,
  receiveBroadcastPerformanceRecipients,
} from 'src/action-creators/hv-broadcasts';
import {receiveAutoReplySettingsUpdates} from 'src/action-creators/chat/phone-number-sets';
import {setInbox} from 'src/action-creators/chat/multi-number-inbox';
import {receiveContact} from 'src/action-creators/contacts';
import {markBroadcastProcessed} from 'src/action-creators/broadcasts';
import {receivePopulatedThreads} from 'src/action-creators/threads';
import {receive as receiveAgentsForThread} from 'src/action-creators/accounts';
import {selectContact} from 'src/selectors/contacts';
import {getAudienceMembers} from 'src/selectors/audience-members';
import {selectQueueSoundSetting} from 'src/selectors/messaging-queues';
import {toastApi} from '@spaced-out/ui-design-system/lib/components/Toast';
import LatNotification from 'src/components/messaging/lat-notification';
import {EXTENSION_ID} from 'src/chat-extension/constants';
import {
  receiveQueueNotificationId,
  removeQueueNotificationId,
} from 'src/action-creators/chat/queues';
import {
  MESSAGE_EVENTS,
  THREAD_UPDATE,
  CONTACT_UPDATE,
  UNREAD_COUNT,
  CONVERSATION_CONTEXT_EVENT,
  TYPING_EVENT,
  READ_STATUS,
  SCHEDULER_NEW,
  SCHEDULER_RESCHEDULE,
  SCHEDULER_CANCEL,
  AGENT_JOINED_THREAD,
  AUTOREPLY_CHANGE,
  CONVERSATION_ADDED_TO_QUEUE,
  BROADCAST_DELIVERY_STATS_EVENT,
  BROADCAST_PERFORMANCE_RECIPIENTS_EVENT,
} from 'src/types/websocket';
import {WebSocketClient} from 'src/web-socket-client';
import {camel} from 'src/utils';
import logger from 'src/utils/logger';
import {captureSentryException} from 'src/utils/sentry';
import {
  pushChatLocation,
  replaceChatLocation,
} from 'src/components/messaging/link';
import type {AudienceMember} from 'src/types/audience-member';
import type {Contact} from 'src/types/contacts';


const WEBSOCKET_OPEN = 1;

// Amount of time we switch WebSocket feature off if the user is experiencing
// issues with the WebSocket
const WEBSOCKET_FORBIDDEN_MS = 6 * 60 * 60 * 1000; // 6 hours

// Export for testing
export class MessageUpdateReceiver {
  client: WebSocketClient;
  dispatch: Dispatch;
  router: Router;
  buffer: WebsocketMessage[];
  buffering: boolean;
  firstDisconnectTime: Date | null | undefined;
  numDisconnects: number;
  getState: GetState;

  constructor(
    websocketClient: WebSocketClient,
    dispatch: Dispatch,
    getState: GetState,
    router: Router,
  ) {
    this.client = websocketClient;
    this.dispatch = dispatch;
    this.buffer = [];
    this.buffering = true;
    this.firstDisconnectTime = null;
    this.numDisconnects = 0;
    this.getState = getState;
    this.router = router;

    (this as any).handleConnectionOpen = this.handleConnectionOpen.bind(this);
    (this as any).handleConnectionClose = this.handleConnectionClose.bind(this);
    (this as any).handleMessage = this.handleMessage.bind(this);

    this.client.addListener('connection:open', this.handleConnectionOpen);
    this.client.addListener('connection:close', this.handleConnectionClose);
    [
      MESSAGE_EVENTS,
      THREAD_UPDATE,
      CONTACT_UPDATE,
      CONVERSATION_CONTEXT_EVENT,
      TYPING_EVENT,
      UNREAD_COUNT,
      READ_STATUS,
      SCHEDULER_NEW,
      SCHEDULER_RESCHEDULE,
      SCHEDULER_CANCEL,
      AGENT_JOINED_THREAD,
      AUTOREPLY_CHANGE,
      CONVERSATION_ADDED_TO_QUEUE,
      BROADCAST_DELIVERY_STATS_EVENT,
      BROADCAST_PERFORMANCE_RECIPIENTS_EVENT,
    ].forEach((type) => {
      this.client.addListener(type, this.handleMessage);
    });

    // The moment we have an open web socket, we begin buffering messages
    // and then fetch the latest updates from the API. Then we empty
    // the buffer. This ensures that we don't miss any message updates, for
    // example, between the moment the app starts and the moment we start
    // getting updates from the web socket.
    if (this.client.connection?.readyState === WEBSOCKET_OPEN) {
      this.handleConnectionOpen();
    }

    //for testing when websocket connection unavailable
    window.__mockWsMessage = (mockMessage: any) => {
      this.handleMessage(mockMessage, true);
    };
  }

  tearDown() {
    this.client.removeListener('connection:open', this.handleConnectionOpen);
    this.client.removeListener('connection:close', this.handleConnectionClose);
    [
      MESSAGE_EVENTS,
      THREAD_UPDATE,
      CONTACT_UPDATE,
      READ_STATUS,
      UNREAD_COUNT,
      SCHEDULER_NEW,
      SCHEDULER_RESCHEDULE,
      SCHEDULER_CANCEL,
      AGENT_JOINED_THREAD,
      AUTOREPLY_CHANGE,
      CONVERSATION_ADDED_TO_QUEUE,
      BROADCAST_DELIVERY_STATS_EVENT,
      BROADCAST_PERFORMANCE_RECIPIENTS_EVENT,
    ].forEach((type) => {
      this.client.removeListener(type, this.handleMessage);
    });
  }

  onEnterReadOnly() {
    if (this.firstDisconnectTime === null) {
      this.firstDisconnectTime = new Date();
    }
    this.numDisconnects++;
    if (typeof localStorage !== 'undefined') {
      // Set variable in localStorage to prevent app from trying to use
      // WebSocket server if user refreshes the page
      localStorage.setItem(
        'doNotUseWebSocketBefore',
        String(new Date(Date.now() + WEBSOCKET_FORBIDDEN_MS)),
      );
    }
  }

  onExitReadOnly() {
    if (this.isConnectionHealthy()) {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('doNotUseWebSocketBefore');
      }
    }
  }

  isConnectionHealthy(): boolean {
    const {firstDisconnectTime} = this;
    if (this.numDisconnects > 5 && firstDisconnectTime) {
      const timeSinceFirstDisconnect =
        Date.now() - firstDisconnectTime.getTime();
      // If disconnect frequency is greater than 1 every 10 seconds ...
      if (this.numDisconnects / timeSinceFirstDisconnect > 1 / 10000) {
        return false;
      }
    }
    return true; // assumed healthy until proven otherwise
  }

  handleConnectionOpen() {
    this.dispatch(changeReadOnlyMode(false));
    this.onExitReadOnly();
    this.getUpdatesFromApi().then(() => {
      this.buffering = false;
      this.buffer.forEach((message) => this.handleMessage(message));
      this.buffer = [];
    });
  }

  handleConnectionClose() {
    this.dispatch(changeReadOnlyMode(true));
    this.onEnterReadOnly();
    this.buffering = true;
  }

  sendMessageToExtension(type: string, payload: any) {
    const chrome = window.chrome;
    if (!chrome || !chrome.runtime) {
      return;
    }
    chrome.runtime.sendMessage(EXTENSION_ID, {
      type,
      payload,
    });
  }

  handleMessage(message: WebsocketMessage, mock: boolean = false) {
    logger.log('got message', message);
    if (!mock && this.buffering) {
      this.buffer.push(message);
    } else {
      switch (message.type) {
        case MESSAGE_EVENTS: {
          const {messages, agents, thread} = message.payload;
          // NOTE (aditya): dispatching these markBroadcastProcessed actions
          // here, so that the broadcast state updates as soon as the websocket
          // message is received.
          // @ts-expect-error TS2304
          const actions: Array<MarkBroadcastProcessed> = [];
          // TOOD (kyle): `messages` should always be defined, right?
          messages &&
            messages.forEach((message) => {
              if (
                // @ts-expect-error - TS2551 - Property 'broadcast_id' does not exist on type 'Message'. Did you mean 'broadcastId'?
                message.broadcast_id &&
                (message.status === 'delivered' || message.status === 'sent')
              ) {
                // @ts-expect-error - TS2551 - Property 'broadcast_id' does not exist on type 'Message'. Did you mean 'broadcastId'?
                actions.push(markBroadcastProcessed(message.broadcast_id));
              }
            });
          const pendingMessages: Array<MessagePending> = values(
            //@ts-expect-error
            this.getState().messages.pendingMessages,
          );
          const filteredMessages = filter(
            messages,
            // @ts-expect-error - TS2339 - Property 'broadcast_id' does not exist on type 'Message'.
            ({direction, broadcast_id, threadId}) =>
              !(
                !broadcast_id &&
                direction === 'outgoing' &&
                pendingMessages.some(
                  (pendingMessage) => pendingMessage?.threadId === threadId,
                )
              ),
          );
          this.dispatch(
            batch(
              this.receiveThreadMessage(message),
              receiveMessages(camel(filteredMessages)),
              receiveAgentsForThread(agents),
              ...actions,
            ),
          );
          break;
        }

        case THREAD_UPDATE: {
          this.dispatch(this.receiveThreadMessage(message));
          break;
        }

        case CONTACT_UPDATE: {
          const {contact} = message.payload;
          this.dispatch(receiveContact(camel(contact)));
          break;
        }

        case UNREAD_COUNT:
          this.dispatch(
            updateUnreadCounts({
              unreadByPhoneNumberSet:
                message.payload.unread_by_phone_number_set,
              unreadByQueue: message.payload.unread_by_lat_queue,
              unreadTimestamp: message.payload.unread_timestamp,
            }),
          );
          break;
        case TYPING_EVENT: {
          const {agent, thread_id, is_typing} = message.payload;
          this.dispatch(receiveTypingEvent(thread_id, agent.id, is_typing));
          break;
        }

        case CONVERSATION_CONTEXT_EVENT: {
          const shouldUseConversationContext =
            this.getState().agentSettings.messagingEnableConversationContext;
          if (shouldUseConversationContext) {
            this.dispatch(
              receiveConversationContextEvents(
                camel(message.payload.event.context_events),
              ),
            );
          }
          break;
        }

        case READ_STATUS: {
          this.dispatch(receiveReadStatusEvent(message.payload));
          break;
        }

        case SCHEDULER_NEW: {
          const {event} = message.payload;
          logger.log('scheduler new event', event);
          const externalPhone = event?.primary_guest_phone;
          if (externalPhone) {
            this.dispatch(receiveSchedulerEvent(externalPhone, event));
          }
          break;
        }
        case SCHEDULER_CANCEL: {
          const {event} = message.payload;
          logger.log('scheduler cancel event', event);
          break;
        }
        case SCHEDULER_RESCHEDULE: {
          const {event} = message.payload;
          logger.log('scheduler reschedule event', event);
          break;
        }

        case AGENT_JOINED_THREAD: {
          const {inbox_id, thread_id} = message.payload;
          this.dispatch(getInbox({})).then(() => {
            const isExtension =
              //@ts-expect-error
              this.getState().env.query.context === 'chrome-extension';
            const pathname = `/messages/queues/${inbox_id}/with/${thread_id}`;
            if (isExtension) {
              replaceChatLocation(this.router, pathname);
            } else {
              pushChatLocation(this.router, pathname);
            }
          });
          break;
        }

        case AUTOREPLY_CHANGE: {
          const {phone_number, active, body, until, is_autoreply_on} =
            message.payload;
          this.dispatch(
            receiveAutoReplySettingsUpdates(phone_number, {
              active,
              body,
              until,
              is_autoreply_on,
            }),
          );
          break;
        }

        case CONVERSATION_ADDED_TO_QUEUE: {
          const path = this.router?.location?.pathname || '';
          if (path.startsWith('/messages') || path.startsWith('/contacts')) {
            const {time_added, inbox_id, queue, position_in_queue} =
              message.payload;
            const {body} = message.metadata;
            const handleDismiss = () => {
              this.dispatch(removeQueueNotificationId());
            };
            const handleOpenQueue = () => {
              this.dispatch(setInboxFilter(null));
              this.dispatch(setInbox(inbox_id));
              const isExtension =
                //@ts-expect-error
                this.getState().env.query.context === 'chrome-extension';
              const pathname = `/messages/queues/${inbox_id}/`;
              if (isExtension) {
                replaceChatLocation(this.router, pathname);
              } else {
                pushChatLocation(this.router, pathname);
              }
            };

            const soundSetting = selectQueueSoundSetting(
              this.getState(),
              queue.id,
            );

            const toastIdFromApi = toastApi.show(
              <LatNotification
                toastBody={body}
                time={time_added}
                positionInQueue={position_in_queue}
                queueName={queue.name}
                soundSetting={soundSetting}
                handleDismiss={handleDismiss}
                handleOpenQueue={handleOpenQueue}
              />,
              {
                autoClose: false,
              },
            );

            this.sendMessageToExtension(UPDATE_AGENT_JOINED_QUEUE, {
              timeAdded: time_added,
              soundSetting,
            });
            // in case of multiple toasts, remove prev and add current
            this.dispatch(removeQueueNotificationId());
            this.dispatch(receiveQueueNotificationId(toastIdFromApi));
          }
          break;
        }
        case BROADCAST_DELIVERY_STATS_EVENT: {
          const {delivery_analytics, broadcast_id} = message.payload;
          this.dispatch(
            receivePerformanceDeliveryAnalytics({
              id: broadcast_id,
              deliveryAnalytics: camel(delivery_analytics),
            }),
          );
          break;
        }
        case BROADCAST_PERFORMANCE_RECIPIENTS_EVENT: {
          const messagePayload = camel(message.payload);
          const {countOnly, id} = messagePayload;
          const omitKeys = countOnly
            ? ['recipients', 'countOnly']
            : ['countOnly'];
          if (id) {
            const payload = omit(messagePayload, omitKeys);
            // @ts-expect-error - TS2345 - Argument of type 'Omit<any, string>' is not assignable to parameter of type 'HVBroadcastPerformanceRecipientsWS'.
            this.dispatch(receiveBroadcastPerformanceRecipients(payload));
          }
          break;
        }

        default:
          const error = new Error(
            // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'never'.
            `MessageUpdateReceiver received invalid message type ${message.type}`,
          );
          logger.error(message, error);
          captureSentryException(error, {
            extra: {
              // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'never'.
              messageType: message.type,
            },
          });
          break;
      }
    }
  }

  getUpdatesFromApi(): Promise<unknown> {
    return this.dispatch(getInbox({noIncrement: true}));
  }

  getFullThreadData(phone_number_set_id: string, external_phone: string) {
    this.dispatch(fetchMessagesForPhone(external_phone, phone_number_set_id));
  }

  receiveThreadMessage(message: ThreadUpdateMsg | NewMessagesMsg): BatchAction {
    const {phone_number_set_id, thread} = message.payload;
    let {contact, audience_members} = message.payload;

    const contacts: Array<Contact> = [];
    const audienceMembers: Array<AudienceMember> = [];

    if (thread && thread.channel === 'lat') {
      this.dispatch(
        // @ts-expect-error - TS2551 - Property 'queue_id' does not exist on type 'Thread'. Did you mean 'queueId'?
        setReceivedPhoneNumberEvent(thread.queue_id, INBOX_TYPE.QUEUE),
      );
      // @ts-expect-error - TS2551 - Property 'sense_phone' does not exist on type 'Thread'. Did you mean 'sensePhone'?
    } else if (thread && thread.sense_phone) {
      this.dispatch(
        // @ts-expect-error - TS2551 - Property 'sense_phone' does not exist on type 'Thread'. Did you mean 'sensePhone'?
        setReceivedPhoneNumberEvent(thread.sense_phone, INBOX_TYPE.PHONE),
      );
    }

    if (message.metadata.is_thin_notification) {
      // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'Contact'. | TS2352 - Conversion of type 'Contact' to type 'string' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
      contact = contact as string;
      // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'AudienceMember[]'. | TS2352 - Conversion of type 'AudienceMember[]' to type 'string[]' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
      audience_members = audience_members as string[];

      const localAudienceMembers = getAudienceMembers(this.getState());
      if (
        // @ts-expect-error - TS2345 - Argument of type 'Contact' is not assignable to parameter of type 'string'.
        (contact && !selectContact(this.getState(), contact)) ||
        // @ts-expect-error - TS2538 - Type 'AudienceMember' cannot be used as an index type.
        audience_members.some((id) => !localAudienceMembers[id])
      ) {
        // @ts-expect-error - TS2551 - Property 'external_phone' does not exist on type 'Thread'. Did you mean 'externalPhone'?
        this.getFullThreadData(phone_number_set_id, thread.external_phone);
      }
    } else {
      if (contact) {
        contacts.push(camel(contact));
      }
      if (audience_members?.length) {
        audienceMembers.push(...camel(audience_members));
      }
    }
    const camelThread = omit(camel(thread), 'lastMessageReadByAgents');

    // @ts-expect-error - TS2740 - Type 'Pick<any, string | number | symbol>' is missing the following properties from type 'Thread': id, sensePhone, count, externalPhone, and 20 more.
    return receivePopulatedThreads([camelThread], contacts, audienceMembers);
  }
}
